/**
 * Course Detail Page
 *
 * Main page for viewing course content with sessions, materials, and purchase flow.
 * Handles authentication, access control, progress tracking, and analytics.
 */

import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  useCourse,
  useCourseSession,
  useStripe,
  getLocalizedTitle,
  useDocumentDirection,
  useContentProtection,
  useUpdateProgress,
} from '@immopersian/common';
import { useTranslation } from 'react-i18next';
import { PurchasePopup } from '../components/PurchasePopup';
import { SessionSidebar } from '../components/SessionSidebar';
import { LoadingScreen } from '../components/LoadingScreen';
import { CourseHeader } from '../components/course/CourseHeader';
import { SessionContent } from '../components/course/SessionContent';
import { LockedSessionPrompt } from '../components/course/LockedSessionPrompt';
import { SelectSessionPrompt } from '../components/course/SelectSessionPrompt';
import {
  trackCourseView,
  trackCoursePurchaseStart,
  trackSessionStart,
  trackError,
} from '../lib/analytics';

export default function CourseDetailPage() {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const { data: course, isLoading: isLoadingCourse } = useCourse(courseId!);
  const { hasAccessToCourse, getCustomerPortalUrl, useCoursePrice, isLoadingPurchases } = useStripe();
  const { data: priceData } = useCoursePrice(courseId);
  const { t } = useTranslation();
  
  useDocumentDirection();
  useContentProtection({
    disableContextMenu: true,
    disableSelection: true,
    disableKeyboardShortcuts: true,
    disableDragDrop: true,
    blurOnFocusLoss: false,
  });
  
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [showPurchasePopup, setShowPurchasePopup] = useState(false);
  const [isPurchaseLoading, setIsPurchaseLoading] = useState(false);
  const trackedCourseRef = useRef<string | null>(null);
  const trackedSessionRef = useRef<string | null>(null);
  
  const { data: selectedSession } = useCourseSession(selectedSessionId || '');
  const updateProgress = useUpdateProgress();
  const { i18n } = useTranslation();

  // Wait for purchases to load before determining access
  const isLoading = isLoadingCourse || isLoadingPurchases;
  const hasAccess = course && !isLoadingPurchases ? hasAccessToCourse(course.id, course.price_id) : false;
  const freeSessions = course?.sessions?.filter(s => s.is_free) || [];
  const hasFreeSessions = freeSessions.length > 0;

  // Track course view
  useEffect(() => {
    if (course && trackedCourseRef.current !== course.id) {
      trackedCourseRef.current = course.id;
      trackCourseView(course.id, getLocalizedTitle(course, i18n.language));
    }
  }, [course, i18n.language]);

  // Track session start and update progress
  useEffect(() => {
    if (selectedSession && trackedSessionRef.current !== selectedSession.id && courseId) {
      trackedSessionRef.current = selectedSession.id;
      trackSessionStart(selectedSession.id, getLocalizedTitle(selectedSession, i18n.language), courseId);
      
      // Update progress - mark session as accessed
      updateProgress.mutate({
        entityType: 'course_session',
        entityId: selectedSession.id,
        progress: 10, // Started
      });
    }
  }, [selectedSession, courseId, i18n.language]);
  
  useEffect(() => {
    // Only run this effect after purchases have loaded
    if (isLoadingPurchases) return;
    
    if (course?.sessions && course.sessions.length > 0 && !selectedSessionId) {
      if (hasAccess) {
        setSelectedSessionId(course.sessions[0].id);
      } else if (hasFreeSessions) {
        const firstFree = course.sessions.find(s => s.is_free);
        if (firstFree) setSelectedSessionId(firstFree.id);
      } else {
        setShowPurchasePopup(true);
      }
    }
  }, [course, hasAccess, hasFreeSessions, selectedSessionId, isLoadingPurchases]);

  const handlePurchase = async () => {
    if (!course) return;
    setIsPurchaseLoading(true);
    
    // Track purchase start
    trackCoursePurchaseStart(
      course.id, 
      getLocalizedTitle(course, i18n.language),
      priceData?.price || 0
    );
    
    try {
      const portalUrl = await getCustomerPortalUrl(course.id);
      window.location.href = portalUrl;
    } catch (error) {
      console.error('Failed to redirect to billing:', error);
      trackError('purchase_redirect_failed', error instanceof Error ? error.message : 'Unknown error');
      alert('Failed to redirect to checkout. Please try again.');
      setIsPurchaseLoading(false);
    }
  };

  const handleSessionClick = (sessionId: string, isFree: boolean) => {
    if (hasAccess || isFree) {
      setSelectedSessionId(sessionId);
    } else {
      setShowPurchasePopup(true);
    }
  };

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!course) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Course not found</div>
      </div>
    );
  }

  // No free sessions and no access - show purchase popup only
  if (!hasAccess && !hasFreeSessions) {
    return (
      <div className="min-h-screen bg-white">
        <PurchasePopup
          course={course}
          onClose={() => navigate('/')}
          onPurchase={handlePurchase}
          isLoading={isPurchaseLoading}
          priceData={priceData}
        />
        <div className="blur-sm pointer-events-none">
          <CourseHeader course={course} navigate={navigate} hasAccess={hasAccess} onUnlock={() => {}} />
        </div>
      </div>
    );
  }

  const selectedSessionData = course.sessions?.find(s => s.id === selectedSessionId);
  const isSelectedSessionAccessible = hasAccess || selectedSessionData?.is_free;

  return (
    <div className="min-h-screen bg-white">
      {showPurchasePopup && (
        <PurchasePopup
          course={course}
          onClose={() => setShowPurchasePopup(false)}
          onPurchase={handlePurchase}
          isLoading={isPurchaseLoading}
          priceData={priceData}
        />
      )}


      <CourseHeader
        course={course}
        navigate={navigate}
        hasAccess={hasAccess}
        onUnlock={() => setShowPurchasePopup(true)}
      />

      <div className="flex flex-col lg:flex-row h-auto lg:h-[calc(100vh-120px)]">
        <SessionSidebar
          sessions={course.sessions || []}
          selectedSessionId={selectedSessionId}
          hasAccess={hasAccess}
          freeSessions={freeSessions}
          onSessionClick={handleSessionClick}
        />

        <div className="flex-1 lg:overflow-y-auto">
          {selectedSession && isSelectedSessionAccessible ? (
            <SessionContent session={selectedSession} />
          ) : selectedSessionId && !isSelectedSessionAccessible ? (
            <LockedSessionPrompt onUnlock={() => setShowPurchasePopup(true)} />
          ) : (
            <SelectSessionPrompt />
          )}
        </div>
      </div>
    </div>
  );
}
